import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const TermOfUseForOrganizers = () => {
  const { currentLang } = useLanguage();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', title: 'Introduction' },
    { id: 'who-we-are', title: '1. Who We Are & What We Do' },
    { id: 'services', title: '2. Your Use of Our Services' },
    { id: 'account', title: '3. Creating an Account' },
    { id: 'content', title: '4. Content of the Event Page' },
    { id: 'ownership', title: '5. Ownership of Content' },
    { id: 'payment', title: '6. Payment' },
    { id: 'cancelled', title: '7. Cancelled, Postponed or Varied Events' },
    { id: 'exchanges', title: '8. Exchanges & Refunds' },
    { id: 'unauthorised', title: '9. Unauthorised Use of the Site' },
    { id: 'termination', title: '10. Termination of Account' },
    { id: 'breach', title: '11. Consequences of a Breach' },
    { id: 'disclaimer', title: '12. Disclaimer & Limitation of Liability' },
    { id: 'indemnity', title: '13. Indemnity' },
    { id: 'general', title: '14. General Provisions' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for header navbar
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100; // Offset for header navbar
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 antialiased selection:bg-indigo-100 selection:text-indigo-900 font-sans">
      <Navbar />
      
      {/* Header Banner */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden font-sans">
        {/* Glow element */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/15 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              Organizer Terms of Use
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-sans">
              Terms and conditions for event organizers and ticket sellers on BookMeIn.Events.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 font-semibold font-sans">
              <SafeIcon name="Clock" className="w-4 h-4 text-indigo-400" />
              Last updated: August 2026
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-1/4">
            <div className="sticky top-28 bg-white/75 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl shadow-sm hidden lg:block">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                <SafeIcon name="Compass" className="w-4 h-4 text-indigo-500" />
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {sections.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all duration-200 block ${
                      activeSection === sec.id
                        ? 'bg-indigo-50 text-indigo-600 border-l-2 border-indigo-600'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'
                    }`}
                  >
                    {sec.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Terms Text Content */}
          <article className="lg:w-3/4 bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 shadow-sm leading-relaxed text-slate-700 font-sans">
            
            {/* Introduction */}
            <div id="intro" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100 flex items-center gap-2">
                <SafeIcon name="FileText" className="w-5 h-5 text-indigo-500" />
                Introduction
              </h2>
              <div className="space-y-4">
                <p>
                  This website (<strong>&ldquo;Site&rdquo;</strong>) is owned and operated by <strong>BMI SOFTWARE S.A.</strong> (BRN 166543403000) trading as <strong>&ldquo;BookMeIn.Events&rdquo;</strong>. Throughout these Organizer Terms, the terms <strong>&ldquo;BookMeIn.Events&rdquo;</strong>, <strong>&ldquo;we&rdquo;</strong>, <strong>&ldquo;us&rdquo;</strong> and <strong>&ldquo;our&rdquo;</strong> refer to BMI SOFTWARE S.A. and its associated entities and <strong>&ldquo;Organizer&rdquo;</strong>, <strong>&ldquo;you&rdquo;</strong> and <strong>&ldquo;your&rdquo;</strong> refer to the individual accepting these Terms and/or any entity that you are acting on behalf of.
                </p>
                <p>
                  These terms and conditions govern the relationship between you and BMI SOFTWARE S.A. Please read this agreement carefully. By creating an account with BMI SOFTWARE S.A. (<strong>&ldquo;account&rdquo;</strong>), you are agreeing to be bound by the following terms (<strong>&ldquo;Terms&rdquo;</strong>) and by the BMI SOFTWARE S.A. <a href="https://bookmein.events/terms-of-use-for-bookeminevents-platform/" className="text-indigo-600 hover:underline">Privacy Policy</a>.
                </p>
                <p>
                  You must not use or access your account or the Site in violation of these Terms. If you choose not to be bound by the Terms then you must not use your account or this Site to advertise or promote any event.
                </p>
                <p>
                  BMI SOFTWARE S.A. may vary these Terms at any time and will post the variations on this Site and/or email the varied terms to you. By continuing to use the site after the terms are varied, you agree to be bound by the variation.
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <div id="who-we-are" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                1. Who We Are and What We Do
              </h2>
              <p className="mb-4">
                BMI SOFTWARE S.A. offers the following services to event organizers (<strong>&ldquo;services&rdquo;</strong>):
              </p>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.1.</span>
                  <span>We allow you to create an event page on our Site from our easy-to-use templates to advertise and promote your event or organization via a unique URL;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.2.</span>
                  <span>We allow you to determine the ticket price and ticket delivery methods;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.3.</span>
                  <span>We give you access to our secure payment gateway (<strong>&ldquo;gateway&rdquo;</strong>) through which people (<strong>&ldquo;ticket buyers&rdquo;</strong>) can buy tickets to your event or buy a membership registration directly from your event page;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.4.</span>
                  <span>We process and hold all payment from Ticket buyers on your behalf so you do not need to set up your own payment processes. You will receive 100% of your designated ticket or registration price less the BMI SOFTWARE S.A.&rsquo; service fee set out in clause 6;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.5.</span>
                  <span>We deliver all tickets to Ticket buyers on your behalf, via our email (<strong>&ldquo;e-ticket&rdquo;</strong>) service;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.6.</span>
                  <span>We maintain accurate records of the number of tickets purchased for your event which we will provide to you on request so that you can easily track how ticket sales for your event are progressing;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.7.</span>
                  <span>If you want to hand over aspects of your event page management to us, or require customized pages and fulfilment processes, we can do this on your behalf for additional fees.</span>
                </li>
              </ul>
            </div>

            {/* Section 2 */}
            <div id="services" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                2. Your Use of Our Services
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.1.</span>
                  <span>BMI SOFTWARE S.A. grants you a non-exclusive, non-transferrable right to access and use the Services solely for the purposes of creating an event page, offering to sell and selling tickets or registrations to an event or organization you have registered on the Site. In consideration of this right, you agree that we may charge a service fee (<strong>&ldquo;service fee&rdquo;</strong>) per ticket as outlined in clause 6.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.2.</span>
                  <span>From time to time, BMI SOFTWARE S.A. may allow you to download or use software in connection with the services (for example, mobile applications or embedding code for your independent website). In those circumstances, BMI SOFTWARE S.A. grants you a non-transferable, revocable, non-exclusive license to use such software solely for your use in connection with the Services and in accordance with these Terms and any instructions provided by BMI SOFTWARE S.A. in conjunction with the software.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.3.</span>
                  <span>Each ticket issued by BMI SOFTWARE S.A. is subject to our website terms and conditions. You acknowledge that you have read and agreed to these user terms insofar as they apply to you and the user of the Site. The user terms make provision for you or the venue operator to set additional terms and conditions specifically related to your event. You must clearly outline any additional terms and conditions on your event page.</span>
                </li>
              </ul>
            </div>

            {/* Section 3 */}
            <div id="account" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                3. Creating an Account
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.1.</span>
                  <span>To access the services, you must create an account on the Site.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.2.</span>
                  <span>You must be at least 18 years of age to create an account. The Site is for the sale of tickets or registrations to an event or organization being conducted in Australia only.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.3.</span>
                  <span>If you are an individual creating an account on behalf of your employer or a company, you acknowledge and represent that you have the legal authority to represent that legal entity and to grant BMI SOFTWARE S.A. the permissions outlined in these Terms.</span>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">3.4.</span>
                    <span>By creating an account, you authorize BMI SOFTWARE S.A. to act as your agent in respect of ticket sales for your event and specifically to:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.1.</span>
                      <span>advertise and promote your event or organization via the event page and the BMI SOFTWARE S.A. website, and as otherwise agreed;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.2.</span>
                      <span>sell tickets or registrations to the public for any event promoted on the event page;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.3.</span>
                      <span>collect GST on ticket sales as applicable;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.4.</span>
                      <span>collect payment from ticket buyers and hold it on your behalf;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.5.</span>
                      <span>retain all relevant service fees; and</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">3.4.6.</span>
                      <span>process and deliver purchased tickets to ticket buyers.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.5.</span>
                  <span>To create your account, you will need to supply a password. It is your sole responsibility to maintain the confidentiality of the password and the activities conducted by your account. BMI SOFTWARE S.A. is not liable for any loss or damage resulting from your failure to maintain a secure account or for any unauthorized modification of the event page.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.6.</span>
                  <span>It is your responsibility to provide and maintain accurate account information, including the username, contact details and banking details, and you agree to notify BMI SOFTWARE S.A. immediately of any change to this information. We are not responsible for deposits into a bank account where incorrect or out of date information have been provided to us.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.7.</span>
                  <span>All personal information you provide will be held in accordance with the BMI SOFTWARE S.A.&rsquo; Privacy Policy.</span>
                </li>
              </ul>
            </div>

            {/* Section 4 */}
            <div id="content" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                4. Content of the Event Page
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">4.1.</span>
                    <span>It is your responsibility to upload all relevant information to the event page and to ensure the content complies with clause 9. You agree that all content you upload is true, accurate and complete, and will be maintained and kept up to date. BMI SOFTWARE S.A. is not liable for a failure on your part to accurately and adequately inform ticket buyers of the terms of their purchase. This includes information regarding:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">4.1.1.</span>
                      <span>the date and time the tickets are available for purchase;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">4.1.2.</span>
                      <span>the payment methods available to ticket buyers;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">4.1.3.</span>
                      <span>refund timeframes in the event of cancellation and/or issuing error (must be consistent with clause 8);</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">4.1.4.</span>
                      <span>whether there is a maximum number of tickets that can be purchased in any one transaction.</span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            {/* Section 5 */}
            <div id="ownership" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                5. Ownership of Content
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.1.</span>
                  <span>You retain ownership of information and content uploaded by you to the event page. However, to enable BMI SOFTWARE S.A. to reproduce the event content on the Site, you grant to BMI SOFTWARE S.A. a non-exclusive, transferable, assignable and sub-licensable licence worldwide to all information, content, graphics, logos, images and other content (<strong>&ldquo;Organiser Content&rdquo;</strong>). This licence permits BMI SOFTWARE S.A. to copy, reproduce, modify, create derivative works, publish or distribute the Organiser Content as it sees fit.</span>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">5.2.</span>
                    <span>You represent, warrant and guarantee that:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">5.2.1.</span>
                      <span>you have all rights necessary to grant the licence contained in clause 5.1;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">5.2.2.</span>
                      <span>the Organiser Content does not infringe the intellectual property or ownership rights of a third party;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">5.2.3.</span>
                      <span>you will not make any attempt to disguise the origin or owner of Organiser Content or the provider of the tickets promoted through the Organiser Content; and</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">5.2.4.</span>
                      <span>you are responsible for the content or reliability of any websites linked via the Organiser Content.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.3.</span>
                  <span>All content, images, software and designs on the Site other than the Organiser Content (<strong>&ldquo;BMI SOFTWARE S.A. Content&rdquo;</strong>) are owned by us or used under licence or with consent, including from other event organisers. You may make a temporary copy of all or part of the Site on your local computer for the sole purpose of reviewing the event page. You may also retain a single permanent copy for your business records. You may not otherwise in any form copy, reproduce, modify, create derivative works, publish or distribute BMI SOFTWARE S.A. Content without the prior written consent of BMI SOFTWARE S.A. or as permitted by applicable copyright laws.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.4.</span>
                  <span>The BMI SOFTWARE S.A. Content is protected by copyright under the laws of Australia and other countries through international treaties. You must not remove any proprietary notice or credits contained on the Site.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.5.</span>
                  <span>The Site contains trade marks, logos and trade names of BookMeIn.Events which are solely property of BMI SOFTWARE S.A. (<strong>&ldquo;BMI SOFTWARE S.A. Trade Marks&rdquo;</strong>) which may be registered or otherwise protected by law. BMI SOFTWARE S.A. Trade Marks will be included on all materials we generate on your behalf, including all tickets and your Event Page. You must not otherwise use any trade marks, logos or trade names which are used on the Site without our prior written consent. Nothing contained on this Site should be construed as granting any license or right to use any trade mark displayed on the Site.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.6.</span>
                  <span>We reserve all of our rights to enforce our intellectual property rights to the fullest extent of the law.</span>
                </li>
              </ul>
            </div>

            {/* Section 6 */}
            <div id="payment" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                6. Payment
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">6.1.</span>
                    <span>When you set up an event page on the Site, you will:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.1.1.</span>
                      <span>identify your BRN or ABN (if applicable) and whether you are registered for both VAT or GST;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.1.2.</span>
                      <span>input the cost of the ticket inclusive of both VAT or GST. Subject to clause 6.2, you determine the ticket price at your sole discretion.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">6.2.</span>
                    <span>In consideration of the services provided to you, BMI SOFTWARE S.A. will charge the Organizer a monthly subscription based on the active plan and additional service fees in respect of every ticket sold &ndash; based on the level of the subscription plans or custom service agreement, as will be specified in a SLA between the 2 parties, when required. Any settle or service fee amount owed to BMI SOFTWARE S.A. will be automatically debited from the organizers account at a set period-of-time upon issuing a collection notice. In case BMI SOFTWARE S.A. handling the payment system, the payments received by the organizers will be less the service fees. The service fee is calculated as follows:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.1.</span>
                      <span>a minimum charge of 1.99% &amp; 0.49 cents (inclusive of VAT or GST) per ticket sold by the Organizer;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.2.</span>
                      <span>(a) Starter Plan: Standard fees will be applied of the total price of each ticket sold by you in case BMI SOFTWARE S.A. payment system is used.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.2.(b)</span>
                      <span>Premium Plan: Reduced fees will be applied of the total price of each ticket sold by you in case BMI SOFTWARE S.A. payment system is used.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.3.(c)</span>
                      <span>Professional Plan: Special fees will be applied of the total price of each ticket sold by you in case BMI SOFTWARE S.A. payment system or pay later option is used.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.4.(d)</span>
                      <span>Custom Plan: Adjusted fees, as agreed upon in the Service Level Agreement (SLA) between us and you, will be applied of the total price of each ticket sold by you in case you utilize the BMI SOFTWARE S.A. payment system and associated features to sell tickets .</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.5.</span>
                      <span>In the case of any customer refunds, the service fee will be charged on top.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.2.6.</span>
                      <span>After three (3) failed debits and corresponding notices, the Organizers BMI SOFTWARE S.A. account will be terminated.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2 pl-6 text-sm text-slate-500 font-semibold italic">
                  <span>In the event that your tickets are sold free of charge no minimum fees will be charged by BMI SOFTWARE S.A.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.3.</span>
                  <span>If pass on feature is chosen by event organizer the services fees add on top of the ticket price. If absorb feature is chosen, service fees amount is absorbed by the event organizers and ticket price payable by the ticket buyer remains the same.</span>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">6.4.</span>
                    <span>In addition to the service fee, the following fees and taxes may be applied by BMI SOFTWARE S.A. as relevant:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.4.1.</span>
                      <span>Postage and handling fee: this will be applied where a ticket buyer requests tickets to be mailed and may include registered post and insurance costs where requested by the ticket buyer;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.4.2.</span>
                      <span>Processing fees may be charged by any payment gateway for credit/debit card transactions as per your country.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">6.4.3.</span>
                      <span>GST.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.5.</span>
                  <span>You are aware that in some circumstances ticket buyers may initiate a credit card chargeback. Any chargeback with respect to your event (processed at any time after a purchase) will be charged back to you and BMI SOFTWARE S.A. is not responsible or liable in any way for chargebacks initiated by Ticket buyers. You indemnify BMI SOFTWARE S.A. for any such chargebacks.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.6.</span>
                  <span>Unless stated to be otherwise, charges referred to for any tickets supplied via the Site are stated inclusive of GST. A receipt will be provided to the purchaser on completion of purchase. Any Tax Invoices which need to be provided to the ticket buyers are the responsibility of organiser as the responsible entity for the event. GST means the goods and services tax charged under the local tax system.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.7.</span>
                  <span>All ticket prices are stated in Eur, US dollar and Australian dollars.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.8.</span>
                  <span>You must raise any objections to any accounting statements from BMI SOFTWARE S.A. within 21 business days after the date the accounting statement is issued to you. If no objection is raised within that period, you are deemed to have accepted the accounting statement.</span>
                </li>
              </ul>
            </div>

            {/* Section 7 */}
            <div id="cancelled" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                7. Cancelled, Postponed or Varied Events
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.1.</span>
                  <span>If you become aware that the details of an event will need to be varied (including time, date, venue etc.) or the event is to be cancelled, you must promptly update the event page.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.2.</span>
                  <span>Unless you otherwise clearly specify in the Event Content, if an event is cancelled, varied or postponed, you agree to offer to ticket buyers a comparable ticket at a rescheduled event, or a refund of the full ticket price.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.3.</span>
                  <span>You must use reasonable endeavors to notify ticket holders of a cancellation, variation or postponement before the date of the event. You will be able to access ticket buyer information from your dedicated administration portal. It is your responsibility to contact the ticket buyer. Any ticket buyer personal information you gather is solely for the purpose of updating them on the cancellation, variation or postponement of an event only. You are not authorized to send ticket buyers any unsolicited emails, mail or &ldquo;spam&rdquo; or to add them to your &lsquo;mailing list&rsquo; (unless they have opted in to receive further correspondence from you).</span>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">7.4.</span>
                    <span>Cancellation of an event:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">7.4.1.</span>
                      <span>BMI SOFTWARE S.A. may charge you an event cancellation fee.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">7.4.2.</span>
                      <span>You are liable to refund any postage and handling fee, service fee or credit card surcharge that the ticket buyer has incurred. Payment of the service fee by you to BMI SOFTWARE S.A. is for the service of facilitating the promotion of the event and the payment process, and it is not contingent upon the event going ahead.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.5.</span>
                  <span>You agree to fully indemnify and hold us harmless against any expenses, costs, loss (including consequential loss) or damage that we may suffer or incur as a result of or in connection with any cancelled, varied or postponed event and consequential refunds to buyers, including any shortfall in refund amounts by reason of the payment by you to us of the service fee or any delay by you in returning monies.</span>
                </li>
              </ul>
            </div>

            {/* Section 8 */}
            <div id="exchanges" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                8. Exchanges and Refunds
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.1.</span>
                  <span>All ticket sales are final and unless you state otherwise on the event page, you are only expected to provide a refund or exchange in accordance with consumer regulations.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.2.</span>
                  <span>Where an error in ticket pricing is identified (whether due to human error or a transactional malfunction of the Site), upon request we will endeavour to cancel the ticket(s) where a refund to the purchase is to be provided.</span>
                </li>
              </ul>
            </div>

            {/* Section 9 */}
            <div id="unauthorised" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                9. Unauthorised Use of the Site
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.1.</span>
                  <span>If we become aware that you have committed, or intend to commit any of the actions below, we may take the action set out in term 11 and immediately and without notice terminate your account and stop payment of any amount that may be owing to you.</span>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">9.2.</span>
                    <span>The following are examples of unauthorized use of the Site and therefore such actions constitute a breach of these Terms. You agree that you will not:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">9.2.1.</span>
                      <span>interfere with the proper working of the Site or take any action that imposes an unreasonably large burden on the Site; or</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">9.2.2.</span>
                      <span>hack into the Site or use robots, spiders or other devices.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">9.3.</span>
                    <span>The following are examples of unauthorized use of your account and therefore such actions constitute a breach of these Terms. You agree that you will not:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">9.3.1.</span>
                      <span>use your account for the promotion of an event where it is not intended that BMI SOFTWARE S.A. facilitate ticket sales through the Site;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">9.3.2.</span>
                      <span>use this Site for the promotion of an event which is not actually intended to take place; or</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">9.3.3.</span>
                      <span>facilitate illegal ticket sales or use the Site to resell tickets where you are not authorized to do so, including by way of unauthorized framing or links to other sites for the purpose of reselling.</span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            {/* Section 10 */}
            <div id="termination" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                10. Termination of Account
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">10.1.</span>
                    <span>BMI SOFTWARE S.A. reserves the right to terminate your account without prior notice where:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">10.1.1.</span>
                      <span>you are in breach of these Terms;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">10.1.2.</span>
                      <span>you are fraudulent in connection with your account;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">10.1.3.</span>
                      <span>there is a failure to pay amounts owed to BMI SOFTWARE S.A. in a timely manner;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">10.1.4.</span>
                      <span>you commit any act of bankruptcy as defined in bankruptcy regulations, make a scheme or arrangement with creditors, or are placed in receivership, liquidation, administration or any form of insolvency administration; or</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">10.1.5.</span>
                      <span>your account remains inactive for a period of 2 months and there is a failure by you to demonstrate an intention to use.</span>
                    </li>
                  </ul>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">10.2.</span>
                  <span>BMI SOFTWARE S.A. may at its sole discretion terminate your account with 30 days&rsquo; notice.</span>
                </li>
              </ul>
            </div>

            {/* Section 11 */}
            <div id="breach" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                11. Consequences of a Breach
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2 flex-col md:flex-row">
                  <div className="flex gap-2">
                    <span className="font-semibold text-indigo-600 shrink-0">11.1.</span>
                    <span>BMI SOFTWARE S.A. reserves the right to monitor all content contained on the Site, including Organizer Content, to ensure compliance with these Terms. If you breach these Terms or we believe you are or were acting in concert with any person who has done so, we may without prior notice:</span>
                  </div>
                  <ul className="pl-6 space-y-2 list-none mt-2">
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">11.1.1.</span>
                      <span>alter, edit or remove any or all Organizer Content;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">11.1.2.</span>
                      <span>delete your account and cancel any tickets or ticket orders that have been made by a purchaser via the account without refund;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">11.1.3.</span>
                      <span>prohibit you from opening future accounts;</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">11.1.4.</span>
                      <span>block or limit your access to the Site; and/or</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-indigo-400 shrink-0">11.1.5.</span>
                      <span>take legal action against you.</span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            {/* Section 12 */}
            <div id="disclaimer" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                12. Disclaimer and Limitation of Liability
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.1.</span>
                  <span>You use the Site and your account at your own risk.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.2.</span>
                  <span>We do not guarantee, represent or warrant that the Site will be free of defects or bugs, or that your access will be uninterrupted, timely or error-free. Your access to the Site and/or your account may be suspended without notice in the case of system failure, maintenance or repair or any reason beyond our control.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.3.</span>
                  <span>We reserve the right to change or discontinue any feature or service on the Site at any time.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.4.</span>
                  <span>To the extent permitted by law, we exclude all conditions and warranties relating to your use of the Site that are not set out in these Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.5.</span>
                  <span>To the extent that our liability for breach of any implied warranty or condition cannot be excluded by law, our liability will be limited, at our option, to the replacement of goods or re-supply of services or the replacement cost of goods or re-supply of services.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.6.</span>
                  <span>In relation to any express warranty or condition set out in these Terms in connection with goods or services supplied or offered by us via the Site, our liability to you will be limited to the amount(s) paid by you (if any) in respect of those goods or services.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">12.7.</span>
                  <span>BMI SOFTWARE S.A. disclaims any and all liability for the acts, omissions and conduct of any third party users, promoters, advertisers and/or sponsors on the Site or otherwise related to your use of the Site.</span>
                </li>
              </ul>
            </div>

            {/* Section 13 */}
            <div id="indemnity" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                13. Indemnity
              </h2>
              <p>
                You agree to fully indemnify and hold us harmless against any expenses, costs, loss (including consequential loss) or damage that we may suffer or incur as a result of or in connection with your use of, or access to or conduct in connection with the Site or your Account, including any breach by you of these Terms.
              </p>
            </div>

            {/* Section 14 */}
            <div id="general" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                14. General Provisions
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">14.1.</span>
                  <span>The Terms shall in all respects be governed by the laws in Greece, Europe. You consent to the exclusive jurisdiction of the courts in Athens to determine any matter or dispute which arises under these Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">14.2.</span>
                  <span>If any provision of the Terms is deemed invalid or unenforceable, all or part of that provision will be severed from the Terms and will not affect the enforceability of the remaining provisions of the Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">14.3.</span>
                  <span>No waiver of any term of the Terms shall be deemed a further or continuing waiver of such term or any other term. Any failure to assert any right under the Terms shall not constitute a waiver of such right.</span>
                </li>
              </ul>
            </div>

          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermOfUseForOrganizers;
